// 8bpp greyscale PNG encoder, for writing team-colour masks.
//
// Why not canvas.toBlob? A canvas is RGBA, so toBlob writes an 8-bit-per-channel
// RGBA (or RGB) PNG — four times the channels a coverage mask needs. Masks are
// uploaded per image and downloaded by every client on every branch, so the
// difference is worth ~60 lines: this writes PNG colour type 0 (greyscale),
// bit depth 8, one byte per texel.
//
// Note that the saving is in the BLOB and the download, not in video memory: no
// runtime PNG decoder produces a single-channel texture, so the client still
// repacks to R8 after decoding (see RemoteConstants.ToSingleChannel).
//
// Deliberately dependency-free — deflate comes from CompressionStream, which is
// in every browser this editor supports (and in Node, which is how it is tested).

const PNG_SIGNATURE = [0x89, 0x50, 0x4e, 0x47, 0x0d, 0x0a, 0x1a, 0x0a];

// PNG colour type 0: each pixel is a single greyscale sample.
const COLOUR_TYPE_GREYSCALE = 0;
const BIT_DEPTH_8 = 8;

// Filter type 0 (None) prefixed to each scanline. Masks are large flat regions,
// so deflate alone compresses them well; the adaptive filters would cost a pass
// over the image for very little on this kind of data.
const FILTER_NONE = 0;

const CRC_TABLE = buildCrcTable();

function buildCrcTable(): Uint32Array {
    const table = new Uint32Array(256);
    for (let n = 0; n < 256; n++) {
        let c = n;
        for (let k = 0; k < 8; k++) {
            c = c & 1 ? 0xedb88320 ^ (c >>> 1) : c >>> 1;
        }
        table[n] = c >>> 0;
    }
    return table;
}

function crc32(bytes: Uint8Array): number {
    let c = 0xffffffff;
    for (let i = 0; i < bytes.length; i++) {
        c = CRC_TABLE[(c ^ bytes[i]) & 0xff] ^ (c >>> 8);
    }
    return (c ^ 0xffffffff) >>> 0;
}

/** A PNG chunk: length, type, data, CRC over type+data. */
function chunk(type: string, data: Uint8Array): Uint8Array {
    const out = new Uint8Array(12 + data.length);
    const view = new DataView(out.buffer);
    view.setUint32(0, data.length);
    for (let i = 0; i < 4; i++) out[4 + i] = type.charCodeAt(i);
    out.set(data, 8);
    view.setUint32(8 + data.length, crc32(out.subarray(4, 8 + data.length)));
    return out;
}

function ihdr(width: number, height: number): Uint8Array {
    const data = new Uint8Array(13);
    const view = new DataView(data.buffer);
    view.setUint32(0, width);
    view.setUint32(4, height);
    data[8] = BIT_DEPTH_8;
    data[9] = COLOUR_TYPE_GREYSCALE;
    data[10] = 0;   // compression: deflate
    data[11] = 0;   // filter method: adaptive (per-scanline byte below)
    data[12] = 0;   // interlace: none
    return data;
}

async function deflate(raw: Uint8Array): Promise<Uint8Array> {
    // 'deflate' is zlib-wrapped (RFC 1950), which is exactly what an IDAT holds.
    // Fed from a one-chunk ReadableStream and drained by hand rather than going
    // through Blob/Response, so the encoder needs only the streams API — which is
    // also all the test environment has to provide.
    const source = new ReadableStream<Uint8Array>({
        start(controller) {
            controller.enqueue(raw);
            controller.close();
        },
    });

    const reader = source.pipeThrough(new CompressionStream('deflate')).getReader();
    const parts: Uint8Array[] = [];
    let total = 0;
    for (; ;) {
        const {done, value} = await reader.read();
        if (done) break;
        parts.push(value);
        total += value.length;
    }

    const out = new Uint8Array(total);
    let at = 0;
    for (const part of parts) {
        out.set(part, at);
        at += part.length;
    }
    return out;
}

/**
 * Encode one byte per pixel as an 8bpp greyscale PNG.
 *
 * @param coverage row-major, exactly width*height bytes (top row first, matching
 *                 ImageData rather than a GL texture).
 */
export async function encodeGreyscalePng(
    coverage: Uint8Array | Uint8ClampedArray,
    width: number,
    height: number,
): Promise<Uint8Array> {
    if (width <= 0 || height <= 0) {
        throw new Error(`Cannot encode a ${width}x${height} PNG`);
    }
    if (coverage.length < width * height) {
        throw new Error(`Expected ${width * height} bytes of coverage, got ${coverage.length}`);
    }

    // Each scanline is prefixed with its filter byte.
    const raw = new Uint8Array(height * (1 + width));
    for (let y = 0; y < height; y++) {
        const rowStart = y * (1 + width);
        raw[rowStart] = FILTER_NONE;
        raw.set(coverage.subarray(y * width, y * width + width), rowStart + 1);
    }

    const parts = [
        new Uint8Array(PNG_SIGNATURE),
        chunk('IHDR', ihdr(width, height)),
        chunk('IDAT', await deflate(raw)),
        chunk('IEND', new Uint8Array(0)),
    ];

    const total = parts.reduce((sum, part) => sum + part.length, 0);
    const png = new Uint8Array(total);
    let at = 0;
    for (const part of parts) {
        png.set(part, at);
        at += part.length;
    }
    return png;
}

/**
 * The same PNG as a File, named so the backend's extension check accepts it
 * (uploadFile keys off the extension and stores the blob under a fresh GUID, so
 * the stem is only ever seen in a network log).
 */
export async function encodeGreyscalePngFile(
    coverage: Uint8Array | Uint8ClampedArray,
    width: number,
    height: number,
    name = 'mask.png',
): Promise<File> {
    const png = await encodeGreyscalePng(coverage, width, height);
    return new File([png as BlobPart], name, {type: 'image/png'});
}
